import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useAssistanceContext } from './AssistanceContext.tsx';
import Explanation from './Explanation.tsx';
import { getValidationCheckMessage, getValidationCheckTargetValue, ValidationCheckResult } from './validationChecks.ts';
import { AlertIcon } from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import classnames from '../../../core/utils/classnames.tsx';

interface ValidationCheckExplanationProps {
    validationCheck: ValidationCheckResult;
    fieldName?: string;
    severity?: string;
    showIcon?: boolean;
    onAction?: () => void;
}

const ValidationCheckExplanation = ({
    validationCheck,
    fieldName,
    severity = 'warning',
    showIcon = true,
    onAction,
}: ValidationCheckExplanationProps) => {
    const { t } = useTranslation('assistance');
    const { readOnly } = useAssistanceContext();

    const isUnitPriceMismatch = validationCheck.name === 'line_item_unit_price_matches_gross_price';
    const isErpPrice = validationCheck.explanationDetails?.price_source === 'erp';
    const message = isErpPrice
        ? t('validationChecks.line_item_unit_price_matches_gross_price_erp')
        : getValidationCheckMessage(t, validationCheck, fieldName);
    const targetValue = getValidationCheckTargetValue(t, validationCheck);

    return (
        <Explanation severity={severity}>
            <div className="flex gap-1.5 items-center">
                {showIcon && <AlertIcon severity={severity} />}

                <span className="whitespace-pre-line">{message}</span>

                {targetValue && <span>{targetValue}</span>}

                {validationCheck.explanationDetails &&
                    validationCheck.explanationDetails?.price_level_name &&
                    isUnitPriceMismatch && (
                        <span>
                            {t(`validationChecks.${validationCheck.name}_details.price_level`)}{' '}
                            {validationCheck.explanationDetails['price_level_name']}{' '}
                        </span>
                    )}

                {validationCheck.explanationDetails &&
                    'price_level_name' in validationCheck.explanationDetails &&
                    !validationCheck.explanationDetails.price_level_name &&
                    isUnitPriceMismatch && (
                        <span>{t(`validationChecks.${validationCheck.name}_details.no_price_level`)} </span>
                    )}
            </div>

            {isUnitPriceMismatch && onAction && (
                <Button
                    onClick={() => onAction()}
                    className={classnames(
                        'text-xs mt-2',
                        severity === 'error' && 'border-line-danger-strong',
                        severity === 'warning' && 'border-line-warning-strong',
                        severity === 'success' && 'border-line-success-strong'
                    )}
                    disabled={readOnly}
                >
                    {t(isErpPrice ? 'assistance:use_erp_price' : 'assistance:use_catalog_price')}
                </Button>
            )}
        </Explanation>
    );
};

export default ValidationCheckExplanation;
